import { attach, createEvent, createStore, sample } from 'effector';
import { PortalServiceChangeEventDto } from '@nimbus/portal-service';
import { postPortalContentClientApi } from '@nx-frontend/shared/api';
import { changeEventsToDto } from '@nx-frontend/shared/dtos';
import { $orgId, $portalId, $sessionId } from '@nx-frontend/shared/store/portal-app';
import {
  ChangeEvent,
  ChangeEventType,
  ChangeUndoCollection,
  MenuItemType,
  PORTAL_CHANGES_CURSOR_STORAGE_KEY,
  PORTAL_CHANGES_HISTORY_STORAGE_KEY,
  PortalBlockType,
  SIDEBAR_CHANGE_TYPES,
} from '@nx-frontend/shared/types';
import { changeEventsRouter } from './change-events-router';
import { processDeleteChatEvents, reduceHistory } from './prepare-change-events-to-publish';

const getChangesCount = (history: ChangeEvent[], currentItemIndex: number): number => {
  const cursorItem = history[currentItemIndex];
  if (cursorItem.batchId) {
    const groupItems = history.filter((historyItem) => historyItem.batchId === cursorItem.batchId);
    return groupItems.length;
  } else {
    return 1;
  }
};

export const getChangesByType = (history: ChangeEvent[], type: ChangeEventType): ChangeEvent[] => {
  return history.filter((change) => change.type === type);
};

const getChangesHistoryStorageKey = (portalId: string) => {
  return PORTAL_CHANGES_HISTORY_STORAGE_KEY + portalId;
};

const getChangesCursorStorageKey = (portalId: string) => {
  return PORTAL_CHANGES_CURSOR_STORAGE_KEY + portalId;
};

const changesCursor = createEvent<number>();
export const changesUndo = createEvent<void>();
export const changesRedo = createEvent<void>();
export const changesPath = createEvent<string>();
export const changeEvent = createEvent<ChangeEvent>();
export const setLastPublishTimeEvent = createEvent<number>();
export const setChangesHistory = createEvent<ChangeEvent[]>();
export const setPublishStateEvent = createEvent<PublishState>();

export enum PublishState {
  INFO = 'info',
  SUCCESS = 'success',
  ERROR = 'error',
}
export interface ChangeEventsState {
  history: ChangeEvent[];
  cursor: number;
  undo: boolean;
  path: string;
  initCursor: number;
}

export interface CurrentChangeEvent {
  event: ChangeEvent;
  path: string;
  undo: boolean;
  initHistory?: ChangeEvent[];
}

interface PublishChangesFxParams {
  workspaceId: string;
  portalId: string;
  history: ChangeEvent[];
}

export const publishChangesFx = attach({
  source: [$orgId, $sessionId],
  effect: ([orgId, sessionId], { workspaceId, portalId, history }: PublishChangesFxParams) => {
    processDeleteChatEvents({ orgId, sessionId, workspaceId, portalId, history });
    const reducedHistory = reduceHistory(history);
    const payload = changeEventsToDto(reducedHistory);
    // return createPortalServiceClient({
    //   basePath: `${env.portalServiceUrl}/v2`,
    // }).ContentApi.apiPostContents({ portalId, payload });
    return postPortalContentClientApi({ portalId, changeEventsDto: payload as PortalServiceChangeEventDto[] });
  },
});

export interface ChangeEventsCursor {
  cursor: number;
  hasChanges: boolean;
  undoSteps: number;
  redoSteps: number;
  updatedAt: number;
  history: ChangeEvent[];
}

export const initialChangeEventsState: ChangeEventsState = {
  history: [],
  cursor: -1,
  undo: true,
  path: '/',
  initCursor: -1,
};

export const $changeEventsState = createStore(initialChangeEventsState)
  .on(setChangesHistory, (state, history) => {
    return { ...state, history };
  })
  .on(changeEvent, (state, event) => {
    const { cursor, history } = state;
    const squash =
      cursor > -1 &&
      event.squashId != null &&
      history[cursor].type === event.type &&
      history[cursor].squashId === event.squashId;

    const newCursor = squash ? cursor : cursor + 1;
    const newEvent = squash ? <ChangeEvent>{ ...history[cursor], newValue: event.newValue } : event;

    return {
      ...state,
      undo: false,
      cursor: newCursor,
      history: [...history.slice(0, newCursor), newEvent],
    };
  })
  .on(changesUndo, (state) => {
    const { cursor } = state;
    return {
      ...state,
      undo: true,
      cursor: Math.max(cursor - 1, -1),
    };
  })
  .on(changesRedo, (state) => {
    const {
      cursor,
      history: { length },
    } = state;
    return {
      ...state,
      undo: false,
      cursor: Math.min(cursor + 1, length - 1),
    };
  })
  .on(changesCursor, (state, cursor) => {
    return { ...state, undo: false, cursor };
  })
  .on(changesPath, (state, path) => {
    return { ...state, path, initCursor: state.cursor };
  })
  .on(publishChangesFx.done, (state) => {
    return { ...state, cursor: -1, history: [] };
  });

export const $currentChangeEvent = $changeEventsState.map<CurrentChangeEvent>(
  ({ history, cursor, undo, path, initCursor }) => {
    return {
      undo,
      event: { ...history[cursor + Number(undo)] },
      path,
      initHistory: initCursor ? history.slice(0, initCursor + 1) : undefined,
    };
  }
);

export const $changeEventsCursor = $changeEventsState.map<ChangeEventsCursor>(({ cursor, history }) => {
  return {
    cursor,
    hasChanges: cursor > -1,
    undoSteps: cursor + 1,
    redoSteps: history.length - cursor - 1,
    updatedAt: history[cursor]?.meta?.updatedAt || 0,
    history: history.slice(0, cursor + 1),
  };
});

export const $historyContentBlockIds = $changeEventsCursor.map((state) => {
  return state.history.reduce((acc, event) => {
    if (
      event.type === ChangeEventType.ADD_BLOCK &&
      (event.newValue.type === PortalBlockType.CONTENT || event.newValue.type === PortalBlockType.NOTE)
    ) {
      const id = 'note#' + event.newValue.noteId;
      if (!acc.includes(id)) {
        acc.push(id);
      }
    }
    if (event.type === ChangeEventType.ADD_MENU_ITEM) {
      const item = event.newValue.item;
      if (item.targetId) {
        let id;
        if (item.type === MenuItemType.NOTES_FOLDER) {
          id = 'notesFolder#' + item.targetId;
        }

        if (item.type === MenuItemType.NOTE) {
          id = 'note#' + item.targetId;
        }
        if (id && !acc.includes(id)) {
          acc.push(id);
        }
      }
    }
    return acc;
  }, <string[]>[]);
});

export const changesUndoFx = attach({
  source: [$changeEventsState],
  effect: ([{ history, cursor }]) => {
    const changesCount = getChangesCount(history, cursor);
    for (let i = 0; i < changesCount; i++) {
      changesUndo();
    }
    return true;
  },
});

export const changesRedoFx = attach({
  source: [$changeEventsState],
  effect: ([{ history, cursor }]) => {
    const changesCount = getChangesCount(history, cursor + 1);
    for (let i = 0; i < changesCount; i++) {
      changesRedo();
    }
    return true;
  },
});

export const changesUndoCollectionFx = attach({
  source: $changeEventsState,
  effect: (state, { collectId }: ChangeUndoCollection) => {
    let cursor = state.cursor;
    while (cursor >= 0) {
      if (state.history[cursor].collectId === collectId) {
        changesUndo();
      } else {
        break;
      }
      cursor--;
    }
    return true;
  },
});

export const changesUndoAllFx = attach({
  source: $changeEventsState,
  effect: (state) => {
    let cursor = state.cursor;
    while (cursor >= 0) {
      changesUndo();
      cursor--;
    }
    return true;
  },
});

export const changesPathFx = attach({
  source: [$portalId],
  effect: ([portalId], { path }: { path: string }) => {
    changesPath(path);
    const historyString = sessionStorage.getItem(getChangesHistoryStorageKey(portalId));
    const cursorString = sessionStorage.getItem(getChangesCursorStorageKey(portalId));
    let state = [];
    if (historyString) {
      state = JSON.parse(historyString);
      setChangesHistory(state);
    }

    if (cursorString) {
      const cursor = Number(cursorString);
      for (let i = 0; i <= cursor; i++) {
        changesCursor(i);
      }
    }
  },
});

export const saveChangesToStorageFx = attach({
  source: [$changeEventsState, $portalId],
  effect: ([state, portalId]) => {
    const stateString = JSON.stringify(state.history);
    sessionStorage.setItem(getChangesHistoryStorageKey(portalId), stateString);
    sessionStorage.setItem(getChangesCursorStorageKey(portalId), state.cursor.toString());
  },
});

export const getHasSidebarChangesFx = attach({
  source: [$changeEventsState],
  effect: ([state]) => {
    const history = [...state.history.slice(0, state.cursor + 1)].filter((item) =>
      SIDEBAR_CHANGE_TYPES.includes(item.type)
    );
    return !!history.length;
  },
});

export const clearChangesStorageFx = attach({
  source: [$portalId],
  effect: ([portalId]) => {
    sessionStorage.removeItem(getChangesHistoryStorageKey(portalId));
    sessionStorage.removeItem(getChangesCursorStorageKey(portalId));
  },
});

$changeEventsState.watch((state) => {
  if (state.history.length) {
    saveChangesToStorageFx();
  }
});

export const $lastPublishTime = createStore(0).on(setLastPublishTimeEvent, (state, value) => value);
export const $publishState = createStore(PublishState.INFO).on(setPublishStateEvent, (state, value) => value);

sample({
  source: publishChangesFx.done,
  target: clearChangesStorageFx,
});

changeEventsRouter();
